import {
  ComponentType,
  Fragment,
  MutableRefObject,
  ReactElement,
  ReactNode,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { TableComponents, TableVirtuosoProps } from 'react-virtuoso';

import { DndContext } from '@dnd-kit/core';
import {
  CellContext,
  ColumnDef,
  Row,
  SortingFnOption,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import styled from 'styled-components';

import { EmptyFiltersState } from '@components/empty-filters-state';
import { EmptySearchState } from '@components/empty-search-state';
import { IconSortAscSmall } from '@components/icons/sort-asc-small';
import { IconSortDescSmall } from '@components/icons/sort-desc-small';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { transitionStandard, transitionSlowest } from '@utils/transitions';

import { ColumnControlProps } from './column-control';
import { SmallColumnIcon } from './column-icon';
import { EditableCell, isInlineEditableType } from './editable-cells';
import { FilterConfig, filterData, getActiveFilters, getFilterConfigIds } from './filters';
import { useFixedEndColumns, useFixedStartColumns } from './fixed-columns';
import { NotSetText, rowHeight, textColumnMaxLength, typeRenderMap } from './renderers';
import { StyledResizer } from './resizer';
import { nonSortableTypes, sorter } from './sorters';
import { TableSpacer, useFixedHeaderContent } from './table-header';
import { TableStore } from './table-store';
import {
  customColumnClassName,
  expandedRowClassName,
  getColumnAccessorFn,
  isEmptyColumnValue,
  stickyEndClassName,
  useColumnTypeMap,
  withoutTitleClassName,
} from './table-utils';
import { useViews } from './toolbar/helpers';
import { Toolbar, ToolbarProps } from './toolbar/toolbar';
import { AvailableColumn, DataItemType, ExpandedRow } from './types';
import {
  fixedColumnWidth,
  minimumColumnSize,
  selectionColumnWidth,
  spacerColumnWidth,
  tableActionPrefix,
  useColumnSizingVariables,
} from './use-column-sizing-vars';
import { DndOverlay, useReorderDnd } from './use-reorder-dnd';
import { VirtualTable, scrolledClassName } from './virtual-table';

type TableRefObject = {
  clearSelected(): void;
};

export const useTableRef = () => {
  const ref = useRef<TableRefObject>({ clearSelected: noop });
  return ref;
};

export type TableProps<DataItem extends DataItemType = DataItemType> = Omit<
  ToolbarProps<DataItem>,
  'table' | 'store' | 'filterCount' | 'filterActions' | 'showingCount' | 'totalCount' | 'filters'
> & {
  data: Array<DataItem>;
  endFixedColumns?: Array<AvailableColumn<DataItem>>;
  ExpandedRow?: ComponentType<DataItem>;
  onSelectChange?(data: Array<DataItem>): void;
  tableRef?: MutableRefObject<TableRefObject>;
  filterProps?: {
    config: FilterConfig;
    addFilterText: string;
    countSuffix?: string;
    showingText?: string;
    isLoading?: boolean;
  };
  searchStr?: string;
  idColumn: keyof DataItem;
  columnControl?: ReactElement<ColumnControlProps<DataItem>>;
  store: TableStore;
  additionalHeaderMenuItems?: (shortId: string) => ReactNode;
  hideToolbar?: boolean;
  isRefetching?: boolean;
  isRowHighlighted?(row: DataItem): boolean;
  emptyState?: ReactNode;
};

export const Table = <DataItem extends DataItemType = DataItemType>({
  availableColumns,
  endFixedColumns = [],
  data,
  onSelectChange,
  ExpandedRow,
  tableRef,
  filterProps,
  searchStr,
  idColumn,
  columnControl,
  store,
  additionalHeaderMenuItems,
  hideToolbar = false,
  hideExport = false,
  hasViews = false,
  isRefetching = false,
  isRowHighlighted,
  emptyState,
}: TableProps<DataItem>) => {
  const { t } = useScopedTranslate(locale);
  const allowExpand = Boolean(ExpandedRow);
  const allowSelect = Boolean(onSelectChange);
  const fixedColumnIndex = [allowSelect, allowExpand].filter(Boolean).length;
  const activeColumnTypeMap = useColumnTypeMap(availableColumns);
  const fixedStartColumns = useFixedStartColumns<DataItem>(allowSelect, allowExpand);
  const fixedEndColumns = useFixedEndColumns<DataItem>(endFixedColumns);
  const {
    isExternalMode,
    useFilters,
    useColumnOrder,
    useSortingState,
    useColumnSizing,
    useColumnVisibility,
    useTableSelection,
    useColumnSync,
  } = store;

  useColumnSync(availableColumns);

  const dataColumns = useMemo<ColumnDef<DataItem, keyof DataItem>[]>(() => {
    return availableColumns.map((column) => {
      return {
        minSize: minimumColumnSize,
        enableSorting:
          column.sortable !== false && column.type && !nonSortableTypes.includes(column.type),
        sortUndefined: 'last',
        id: column.id,
        type: column.type,
        accessorFn: getColumnAccessorFn(column),
        header: ({ column: tableColumn }) => {
          const sort = tableColumn.getIsSorted();
          const sortIcon =
            sort === 'asc' ? <IconSortAscSmall /> : sort === 'desc' ? <IconSortDescSmall /> : null;
          const icon =
            column.renderHeaderIcon?.() ?? (column.type && <SmallColumnIcon type={column.type} />);
          return (
            <Stack orientation="horizontal" align="center" space={1}>
              {icon}
              <div>{column.title}</div>
              {sortIcon}
            </Stack>
          );
        },
        cell: ({ row }: CellContext<DataItem, keyof DataItem>) => {
          const cellValue = row.original[column.id];
          if (column.onEdit && isInlineEditableType(column.type)) {
            return <EditableCell column={column} row={row.original} value={cellValue} />;
          }
          if (isEmptyColumnValue(cellValue)) {
            if (column.renderFallback) {
              return column.renderFallback(row.original);
            }
            return (
              <NotSetText
                maxLength={column.type === 'text' ? textColumnMaxLength : undefined}
                onSave={column.onEdit ? (value) => column.onEdit?.(row.original, value) : undefined}
              />
            );
          }
          return column.Cell ? (
            <column.Cell row={row.original} />
          ) : (
            // else, if the column type is specified, we default to the type renderer
            (column.type && typeRenderMap[column.type]?.(cellValue, column, row.original)) ??
              // else we just show the value of the column property of the row
              cellValue
          );
        },
        sortingFn: 'sorter' as SortingFnOption<DataItem>,
      } as ColumnDef<DataItem, keyof DataItem>;
    });
  }, [availableColumns]);

  const columns = useMemo(() => {
    return fixedStartColumns.concat(dataColumns).concat(fixedEndColumns);
  }, [fixedStartColumns, fixedEndColumns, dataColumns]);

  const { filters, actions: filterActions } = useFilters();
  const { activeFilterConfigIds } = useViews();

  const allFilters = filters.appliedFilters;

  // Exclude filters for disabled/removed attributes so the counter and rendering stay consistent.
  // activeFilterConfigIds is computed once by the views manager and shared via context.
  const activeFilters = useMemo(() => {
    const configIds =
      activeFilterConfigIds ??
      (filterProps?.config ? getFilterConfigIds(filterProps.config) : undefined);
    if (!configIds) return allFilters;
    return getActiveFilters(allFilters, configIds);
  }, [allFilters, activeFilterConfigIds, filterProps?.config]);

  const activeFilterState = useMemo(
    () => ({ ...filters, appliedFilters: activeFilters }),
    [filters, activeFilters]
  );
  const filterCount = activeFilters.length;

  const filteredData = useMemo(() => {
    if (isExternalMode || !filterProps?.config) {
      return data;
    }

    return filterData(
      filterProps.config,
      data,
      filters.appliedFilters,
      filters.conjunction,
      filterActions
    );
  }, [
    isExternalMode,
    filterProps?.config,
    data,
    filters.appliedFilters,
    filters.conjunction,
    filterActions,
  ]);

  const { selectionState, setSelection } = useTableSelection({ data, idColumn, onSelectChange });
  const { sortingState, setSortingState } = useSortingState();
  const { columnVisibility, setColumnVisibility } = useColumnVisibility();
  const { columnSizing, setColumnSizing } = useColumnSizing();
  const { columnOrder, setColumnOrder } = useColumnOrder();

  const columnOrderWithTableActions = useMemo(() => {
    const newOrder = [...columnOrder];

    if (allowExpand) {
      newOrder.unshift(`${tableActionPrefix}-expand`);
    }

    if (allowSelect) {
      newOrder.unshift(`${tableActionPrefix}-select`);
    }

    return newOrder;
  }, [allowExpand, allowSelect, columnOrder]);

  const table = useReactTable({
    data: filteredData,
    columns,
    defaultColumn: {
      minSize: minimumColumnSize,
    },
    getRowId: (row) => row[idColumn],
    enableExpanding: true,
    enableRowSelection: true,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setSelection,
    onSortingChange: setSortingState,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: isExternalMode ? undefined : getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getColumnCanGlobalFilter: () => true,
    sortingFns: {
      sorter: (rowA, rowB, columnId) => {
        return sorter({ rowA, rowB, columnId, activeColumnTypeMap, allFilters });
      },
    },
    globalFilterFn: 'includesString',
    state: {
      columnOrder: columnOrderWithTableActions,
      columnSizing,
      globalFilter: searchStr,
      columnVisibility,
      sorting: sortingState,
      rowSelection: selectionState,
    },
  });

  const isResizing = Boolean(table.getState().columnSizingInfo.isResizingColumn);
  const { rows } = table.getRowModel();

  // For the virtualizer to work for expanded rows, we handle it
  // like a separated row that we don't render if it is not expanded
  const rowsWithExpanded: Array<ExpandedRow<DataItem> | Row<DataItem>> = useMemo(() => {
    if (!allowExpand) {
      return rows;
    }

    const withExpanded: Array<ExpandedRow<DataItem> | Row<DataItem>> = [];

    rows.forEach((row) => {
      withExpanded.push(row);
      if (row.getIsExpanded()) {
        withExpanded.push({ row, isExpanded: true, isSelected: row.getIsSelected() });
      }
    });

    return withExpanded;
  }, [rows, allowExpand]);

  useImperativeHandle(tableRef, () => ({
    clearSelected: () => setSelection({}),
  }));

  const { sizingVariables } = useColumnSizingVariables(table, columnSizing);

  const { draggingId, ...dndProps } = useReorderDnd({ columnOrder, setColumnOrder });

  const fixedHeaderContent = useFixedHeaderContent({
    table,
    store,
    availableColumns,
    activeColumnTypeMap,
    fixedColumnIndex,
    fixedEndColumnsLength: fixedEndColumns.length,
    additionalHeaderMenuItems,
    filterConfig: filterProps?.config,
    filterActions,
    translations: {
      hideField: t('hideField'),
      moveToStart: t('moveToStart'),
      moveToEnd: t('moveToEnd'),
      filterByThisField: t('filterByThisField'),
    },
  });

  // For our custom TableRow component to read
  const tableContext: VirtualTableContext<DataItem> = {
    rowsWithExpanded,
    ExpandedRow,
    fixedColumnIndex,
    numColumns: table.getVisibleFlatColumns().length,
    isRowHighlighted,
  };

  const emptyContent =
    rowsWithExpanded.length === 0 && Boolean(searchStr) ? (
      <EmptySearchState hasFilters={filterCount > 0} />
    ) : rowsWithExpanded.length === 0 && filterCount > 0 ? (
      <EmptyFiltersState />
    ) : data.length === 0 && emptyState ? (
      emptyState
    ) : null;

  return (
    <Fragment>
      {isRefetching && <Spinner />}
      <TableContainer
        $isDragging={draggingId !== undefined}
        $allowExpand={allowExpand}
        $allowSelect={allowSelect}
        style={{
          ...sizingVariables,
          cursor: isResizing ? 'col-resize' : 'auto',
          opacity: isRefetching ? 0.8 : 1,
        }}
      >
        {!hideToolbar && (
          <ToolbarContainer>
            <Toolbar
              availableColumns={availableColumns}
              table={table}
              store={store}
              hideExport={hideExport}
              filterCount={filterCount}
              filterActions={filterActions}
              filters={activeFilterState}
              filterProps={filterProps}
              columnControl={columnControl}
              showingCount={rows.length}
              totalCount={data.length}
              countSuffix={filterProps?.countSuffix}
              showingText={filterProps?.showingText}
              hasViews={hasViews}
            />
          </ToolbarContainer>
        )}
        <DndContext {...dndProps}>
          <DndOverlay />
          <ScrollContainer>
            {emptyContent ? (
              <Fragment>
                <table>
                  <thead>{fixedHeaderContent()}</thead>
                </table>
                {emptyContent}
              </Fragment>
            ) : (
              <VirtualTable
                defaultItemHeight={57}
                overscan={overscan}
                context={tableContext}
                table={table}
                isResizing={isResizing}
                totalCount={rowsWithExpanded.length}
                components={COMPONENTS}
                resetScrollOnDataChange={isExternalMode}
                fixedHeaderContent={fixedHeaderContent}
              />
            )}
          </ScrollContainer>
        </DndContext>
      </TableContainer>
    </Fragment>
  );
};

const border = `${pxToRem(1)} solid ${theme.color.border10}`;

const TableContainer = styled.div<{
  $allowSelect: boolean;
  $allowExpand: boolean;
  $isDragging: boolean;
}>(({ $allowExpand, $allowSelect, $isDragging }) => {
  let expanderIndent = spacerColumnWidth;
  let stickyColumnIndent = spacerColumnWidth;

  if ($allowExpand) {
    stickyColumnIndent += fixedColumnWidth;
  }

  if ($allowSelect) {
    stickyColumnIndent += selectionColumnWidth;
    expanderIndent += selectionColumnWidth;
  }

  return {
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    // Display border on sticky columns when scrolled
    [`.${scrolledClassName} .${stickyEndClassName}`]: {
      boxShadow: `1px 0px 1px 0px rgba(0, 0, 0, 0.12),
        3px 0px 0px 0px rgba(0, 0, 0, 0.06),
        6px 0px 0px 0px rgba(0, 0, 0, 0.06)`,
    },
    table: {
      position: 'relative',
      backgroundColor: theme.color.backgroundLightDark,
      minWidth: '100%',
      width: 'calc(var(--table-size) * 1px)',
      color: theme.color.textPrimary,
      borderCollapse: 'separate',
      tableLayout: 'fixed',
      borderSpacing: 0,

      thead: {
        position: 'sticky',
        top: 0,
        [`.${customColumnClassName}`]: {
          [`&.${withoutTitleClassName}`]: {
            justifyContent: 'center',
          },
        },
      },

      tr: {
        transition: transitionStandard('all'),
        backgroundColor: theme.color.backgroundLightDark,
        '&[data-is-selected=true]': {
          '> td > div': {
            backgroundColor: theme.color.blueTrans5,
          },
        },
        '&[data-is-highlighted=true]': {
          '> td > div': {
            backgroundColor: theme.color.uiTrans5,
          },
        },
        td: {
          borderTop: border,
        },
        '&:last-child': {
          td: {
            borderBottom: border,
          },
        },
        [`&.${expandedRowClassName} td`]: {
          borderTop: 'none',
        },
      },

      'tbody tr': {
        height: rowHeight,
        [`&:hover, &:hover + .${expandedRowClassName}`]: {
          'td > div': {
            backgroundColor: theme.color.uiTrans10,
          },
        },
        [`&:has(+ .${expandedRowClassName}:hover)`]: {
          'td > div': {
            backgroundColor: theme.color.uiTrans10,
          },
        },
      },

      'tbody tr:first-child td': {
        borderTop: 'none',
      },

      'th, td': {
        zIndex: 1,
        backgroundColor: theme.color.backgroundLightDark,
        height: 'inherit',
        padding: 0,

        '> div': {
          backgroundColor: 'inherit',
          height: rowHeight,
          display: 'flex',
          alignItems: 'center',
          [`*:not(${StyledResizer})`]: {
            overflow: 'hidden',
            overflowWrap: 'unset',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxHeight: '100%',
            maxWidth: '100%',
          },
        },

        '&.fill-width': {
          width: 'auto',
        },

        '&.table-spacer': {
          position: 'sticky',
          width: spacerColumnWidth,
          left: 0,
        },
        ...(!$isDragging && {
          [`&.${stickyEndClassName}`]: {
            position: 'sticky',
            left: stickyColumnIndent,
          },
          '&:has(.table-selection)': {
            position: 'sticky',
            left: spacerColumnWidth,
          },
          '&:has(.table-expander)': {
            position: 'sticky',
            left: expanderIndent,
          },
        }),
      },

      // When hovering over the first column, we need to change the
      // background of the left spacer so it "looks" like its a part
      // of the column. Only applicable if the first column is a sticky
      // since we dont show any hover effect on the column in case of
      // the expander or selection columns.
      'th:first-child:has(+ .sticky-end:hover)': {
        backgroundColor: theme.color.uiTrans5,
      },

      th: {
        borderBottom: border,
      },

      'td > div': {
        padding: `0 ${pxToRem(8)}`,
      },

      '.table-spacer': {
        transition: transitionSlowest('background-color'),
      },

      '.table-expander': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },

      '.table-selection': {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },

      'td[data-column-id="table-action-options"]': {
        div: {
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
    },
    '.table-space-fill': {
      width: 'auto',
    },
    '.scrolled .table-space-fill': {
      display: 'none',
    },
  };
});

const ToolbarContainer = styled(Stack).attrs({
  space: 2,
})({
  padding: `${pxToRem(8)} ${pxToRem(24)}`,
  '.separator': {
    height: pxToRem(16),
  },
  backgroundColor: theme.color.backgroundLightDark,
  borderBottom: `1px solid ${theme.color.border10}`,
});

const ScrollContainer = styled.div({
  position: 'relative',
  flex: 1,
  width: '100%',
  overflowX: 'auto',
});

const locale = {
  en: {
    showing: 'Showing {showing} / {total} {suffix}',
    filter: 'Filter',
    hideField: 'Hide field',
    moveToStart: 'Move to start',
    moveToEnd: 'Move to end',
    selectRow: 'Select row',
    selectAllRows: 'Select all rows',
    filterByThisField: 'Filter by this field',
  },
  is: {
    showing: 'Sýni {showing} / {total} {suffix}',
    filter: 'Sía',
    hideField: 'Fela dálk',
    moveToStart: 'Færa dálk fremst',
    moveToEnd: 'Færa dálk aftast',
    selectRow: 'Velja',
    selectAllRows: 'Velja alla',
    filterByThisField: 'Sía eftir þessu',
  },
};

type VirtualTableContext<T extends DataItemType = DataItemType> = Pick<
  TableProps<T>,
  'ExpandedRow' | 'isRowHighlighted'
> & {
  rowsWithExpanded: Array<Row<T> | ExpandedRow<T>>;
  fixedColumnIndex: number;
  numColumns: number;
};

const CustomTableRow: TableComponents<DataItemType, VirtualTableContext>['TableRow'] = ({
  context,
  ...rest
}) => {
  if (!context) return null;

  const { rowsWithExpanded, fixedColumnIndex, ExpandedRow, numColumns, isRowHighlighted } = context;

  const index = rest['data-index'];
  const row = rowsWithExpanded[index];

  if ('isExpanded' in row) {
    if (!ExpandedRow) return null;

    return (
      <tr
        {...rest}
        key={`${row.row.id}-expanded`}
        className={expandedRowClassName}
        data-is-selected={row.isSelected}
      >
        <TableSpacer />
        <td colSpan={numColumns}>
          <div>
            <ExpandedRow {...row.row.original} />
          </div>
        </td>
        <TableSpacer />
        <TableSpacer fill />
      </tr>
    );
  }

  const isSelected = row.getIsSelected();
  const isHighlighted = isRowHighlighted?.(row.original) ?? false;

  return (
    <tr
      {...rest}
      key={row.original.id}
      data-is-selected={isSelected}
      data-is-highlighted={isHighlighted}
    >
      <TableSpacer />
      {row.getVisibleCells().map((cell, cellIndex) => {
        const className = cellIndex === fixedColumnIndex ? stickyEndClassName : undefined;
        return (
          <td
            key={cell.id}
            className={className}
            data-column-id={cell.column.id}
            data-is-selected={isSelected}
          >
            <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
          </td>
        );
      })}
      <TableSpacer />
      <TableSpacer fill />
    </tr>
  );
};

const COMPONENTS: TableComponents<DataItemType, VirtualTableContext> = {
  TableRow: CustomTableRow,
};

const overscan: TableVirtuosoProps<DataItemType, VirtualTableContext>['overscan'] = {
  main: 300,
  reverse: 300,
};
